import { memo, useMemo } from 'react';
import { motion } from 'framer-motion';

interface DimensionBarProps {
  dimension: number;
  value: number;
  min: number;
  max: number;
  isAxisDimension?: boolean;
  axisLabel?: '1' | '2' | '3';  // 3D axis indicator (1, 2, 3)
  name?: string;  // Dimension name like "Human-Centric ←→ AI-Driven"
  onClick?: () => void;
}

// Get light muted color for axis badges
function getAxisBadgeColor(label: '1' | '2' | '3'): string {
  switch (label) {
    case '1': return 'bg-rose-100 text-rose-400 border-rose-200';
    case '2': return 'bg-emerald-100 text-emerald-400 border-emerald-200';
    case '3': return 'bg-sky-100 text-sky-400 border-sky-200';
  }
}

// Get color based on value (negative = cool blue/purple, positive = warm orange/yellow)
function getBarColor(normalizedValue: number): string {
  // normalizedValue: -1 to 1
  if (normalizedValue < 0) {
    // Cool colors for negative: purple to blue
    const intensity = Math.abs(normalizedValue);
    const hue = 260 - intensity * 40; // 260 (purple) to 220 (blue)
    return `hsl(${hue}, 70%, ${50 + intensity * 20}%)`;
  } else {
    // Warm colors for positive: yellow to orange to red
    const intensity = normalizedValue;
    const hue = 45 - intensity * 30; // 45 (yellow) to 15 (orange-red)
    return `hsl(${hue}, 90%, ${55 - intensity * 10}%)`;
  }
}

export const DimensionBar = memo(function DimensionBar({
  dimension,
  value,
  min,
  max,
  isAxisDimension = false,
  axisLabel,
  name,
  onClick
}: DimensionBarProps) {
  const { normalizedValue, color } = useMemo(() => {
    const range = max - min || 1;
    const midpoint = (max + min) / 2;
    
    // Normalize to -1 to 1 based on data range
    const normalized = (value - midpoint) / (range / 2);
    const clampedNormalized = Math.max(-1, Math.min(1, normalized));
    
    return {
      normalizedValue: clampedNormalized,
      color: getBarColor(clampedNormalized)
    };
  }, [value, min, max]);


  return (
    <motion.div
      className={`group py-2.5 px-3 rounded-lg transition-all cursor-pointer hover:bg-warm-surface-hover/30 ${
        isAxisDimension ? 'bg-warm-surface-hover/20' : ''
      }`}
      onClick={onClick}
      whileHover={{ x: 1 }}
      transition={{ duration: 0.15 }}
    >
      {/* Header: dimension number (or axis badge for 1-3), and value */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {axisLabel ? (
            /* For axis dimensions (1, 2, 3), show colored badge instead of plain number */
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${getAxisBadgeColor(axisLabel)}`}>
              {dimension}
            </span>
          ) : (
            /* For other dimensions, show plain number */
            <span className="text-[11px] font-semibold text-text-secondary/70">
              {dimension}
            </span>
          )}
        </div>
        
        {/* Value display */}
        <span className={`text-[11px] font-mono ${
          normalizedValue > 0.3 ? 'text-orange-primary' : 
          normalizedValue < -0.3 ? 'text-blue-400' : 
          'text-text-secondary/60'
        }`}>
          {value >= 0 ? '+' : ''}{value.toFixed(2)}
        </span>
      </div>
      
      {/* Dimension name - split into left ←→ right */}
      {name && (() => {
        const parts = name.split('←→').map(s => s.trim());
        if (parts.length === 2) {
          return (
            <div className="flex items-center justify-between gap-2 text-xs mb-2">
              <span className="text-text-primary/70 truncate flex-1">{parts[0]}</span>
              <span className="text-text-secondary/40 flex-shrink-0">←→</span>
              <span className="text-text-primary/70 truncate flex-1 text-right">{parts[1]}</span>
            </div>
          );
        }
        return (
          <div className="text-xs text-text-primary/80 leading-snug mb-2" title={name}>
            {name}
          </div>
        );
      })()}

      {/* Bar container */}
      <div className="relative">
        <div className="h-2.5 bg-warm-border/20 rounded-full overflow-hidden relative">
          {/* Center line marker - subtle */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-warm-border/30" />
          
          {/* Value bar */}
          <motion.div
            className="absolute top-0 bottom-0 rounded-full"
            style={{
              backgroundColor: color,
              left: normalizedValue < 0 ? `${50 + normalizedValue * 50}%` : '50%',
              width: `${Math.abs(normalizedValue) * 50}%`,
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
        
      </div>
    </motion.div>
  );
});
